import Image from "next/image";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { KoalaProduct } from "src/types/koala/KoalaProduct";
import koalaUtils from "src/helpers/koala/koalaUtils";
import classNames from "src/helpers/classNames";
import { eq } from "lodash/fp";
import useCountryData from "src/hooks/useCountryData";

interface KoalaProductItemWithDescProps {
  product: KoalaProduct;
}

const KoalaProductItemWithDesc = ({ product }: KoalaProductItemWithDescProps) => {
  const t = useTranslations("ProductViewer");
  const countryData = useCountryData();

  const getProductProperties = (handle: string) => {
    switch (handle) {
      case "havana":
        return {
          categoryTitle: "practical",
          categoryDescription: "practical_description",
        };
      case "bahia":
        return {
          categoryTitle: "elegant",
          categoryDescription: "elegant_description",
        };
      case "alexandria":
        return {
          categoryTitle: "attractive",
          categoryDescription: "attractive_description",
        };
      case "ipanema":
        return {
          categoryTitle: "simple",
          categoryDescription: "simple_description",
        };
      case "copacabana":
        return {
          categoryTitle: "invisible",
          categoryDescription: "invisible_description",
        };
      case "waikiki":
        return {
          categoryTitle: "teen_basic",
          categoryDescription: "teen_basic_description",
        };
      case "wash-bag":
        return {
          categoryTitle: "wash_bag",
          categoryDescription: "wash_bag_description",
        };
      case "travel-bag":
        return {
          categoryTitle: "wet_bag",
          categoryDescription: "wet_bag_description",
        };
      default:
        break;
    }
  };

  const category = getProductProperties(product.handle);

  const getDiscountBanner = () => {
    if (product.name == "Bahia" || product.name == "Ipanema") {
      return <p className="mt-2 rounded bg-red-400 px-2 py-1 text-xs font-bold text-white">{t("discount")}!</p>;
    }
  };

  /**
   * First variant available
   */
  const variant = product.variants.find(koalaUtils.isVariantAvailable);
  const variantImages = variant?.images || product.images;
  const isTeen = countryData && product.collectionIds.includes(countryData.koalaCollections.COLLECTION_TEENS.id);

  return (
    <Link
      href={{
        pathname: `/product/${product.handle}`,
        query: variant?.selectedOptions.reduce((acc, { name, value }) => Object.assign(acc, { [name]: value }), {}),
      }}
      className={`products-list__${product.handle} product-item flex h-full flex-col rounded-lg bg-white p-4 transition duration-300 ease-in-out hover:shadow-xl lg:flex-row lg:justify-between`}
    >
      <div className="order-2 flex flex-col justify-between pt-4 lg:order-1 lg:pe-2 lg:pt-0">
        <p className="text-xl font-bold lg:text-xl">{product.name}</p>
        {getDiscountBanner()}
        <div className="space-y-1 pt-4 lg:pt-0">
          <p className="text-xs font-semibold lg:text-sm">{t(category?.categoryTitle)}</p>

          <p className="text-xs">{t(category?.categoryDescription)}</p>
        </div>
      </div>
      <Image
        src={variantImages[0].url}
        alt="Product image"
        className={classNames(
          "order-1 aspect-[1/1] w-full animate-pulse-bg rounded-lg object-cover lg:order-2 lg:w-1/2",
          isTeen ? "object-top" : ""
        )}
        sizes="
        (min-width: 20em) 11rem,
        (min-width: 27em) 19rem,
        (min-width: 64em) 8rem,
        50vw"
        width={0}
        height={0}
      />
    </Link>
  );
};

export default KoalaProductItemWithDesc;
